
import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useLocation } from "react-router-dom";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signUp: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const SafeAuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log("SafeAuthProvider: Setting up auth state listener");
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        console.log("Auth state change:", { 
          event, 
          hasSession: newSession ? "session exists" : "no session",
          currentPath: location.pathname 
        });
        setSession(newSession);
        setUser(newSession?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("Initial session check:", { 
        hasSession: session ? "session exists" : "no session",
        currentPath: location.pathname 
      });
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [location.pathname]);

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true);
      
      const authOptions: any = { email, password };
      if (captchaToken) {
        authOptions.options = { captchaToken };
      }
      
      const { error } = await supabase.auth.signInWithPassword(authOptions);
      if (error) throw error;
      
      toast({
        title: "Welcome back!",
        description: "You have been signed in successfully.",
      });
      
      // Only redirect if user is on auth page or root page, not if they're on settings
      const currentPath = location.pathname;
      console.log("SignIn navigation check:", { 
        currentPath, 
        shouldRedirect: currentPath === "/auth" || currentPath === "/" 
      });
      
      if (currentPath === "/auth" || currentPath === "/") {
        console.log("Redirecting to dashboard after sign in");
        navigate("/dashboard");
      } else {
        console.log("Not redirecting - user is on:", currentPath);
      }
      
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Sign in failed",
        description: error.message || "An error occurred during sign in.",
      });
      console.error("Error signing in:", error);
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, captchaToken?: string) => {
    try {
      setLoading(true);
      
      const authOptions: any = { email, password };
      if (captchaToken) {
        authOptions.options = { captchaToken };
      }
      
      const { error } = await supabase.auth.signUp(authOptions);
      if (error) throw error;
      
      toast({
        title: "Account created!",
        description: "Please check your email to confirm your account.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message || "An error occurred during sign up.",
      });
      console.error("Error signing up:", error);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log("Sign out initiated from:", location.pathname);
      
      // Don't set loading to true immediately - let the auth state change handle it
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      // Don't manually clear state here - let onAuthStateChange handle it
      console.log("Sign out successful");
      
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
      
      // Navigate to home after successful sign out
      console.log("Redirecting to home after sign out");
      navigate("/");
      
    } catch (error: any) {
      console.error("Error signing out:", error);
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: error.message || "An error occurred during sign out.",
      });
    }
  };

  const value = {
    session,
    user,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
